/**
 * src/types/charts/pie.chart.ts
 * ─────────────────────────────────────────────────────────────────────────────
 * Pie / donut chart — types, spacing, animation styles, and defaults.
 */

import type { ChartDataPoint } from '../core/interfaces.shared';
import { TEXT_STYLE_DEFAULTS } from '../core/interfaces.shared';
import {
  makeTitle, makeSubtitle,
  DEFAULT_BACKGROUND, DEFAULT_LEGEND, DEFAULT_DATA_LABELS,
} from '../core/defaults.shared';
import type { ChartConfigBase } from '../core/chart-config.base';
import type { SchemaNote } from '../chart.schema';

// ─────────────────────────────────────────────────────────────────────────────
// ANIMATION STYLES
// ─────────────────────────────────────────────────────────────────────────────

export type PieAnimationStyle =
  | 'classic' | 'sequential' | 'growFromCenter' | 'spinIn' | 'unfold'
  | 'separation' | 'popIn' | 'wipe' | 'valueOrder';

export const PIE_ANIMATION_STYLES: PieAnimationStyle[] = [
  'classic', 'sequential', 'growFromCenter', 'spinIn', 'unfold',
  'separation', 'popIn', 'wipe', 'valueOrder',
];

export const PIE_ELEMENT_PANEL_STYLES: PieAnimationStyle[] = PIE_ANIMATION_STYLES;


// ─────────────────────────────────────────────────────────────────────────────
// DATA POINT
// ─────────────────────────────────────────────────────────────────────────────

export interface PieDataPoint extends ChartDataPoint {
  chartType:     'pie';
  value:         number;
  showValue?:    boolean;   // whether to draw the value/percentage label for this slice
  labelOffsetX?: number;    // pixel nudge for value/percentage label. Default: 0.
  labelOffsetY?: number;
  nameOffsetX?:  number;    // pixel nudge for name label. Default: 0.
  nameOffsetY?:  number;
}


// ─────────────────────────────────────────────────────────────────────────────
// SPACING CONFIG
// ─────────────────────────────────────────────────────────────────────────────

export interface PieSpacingConfig {
  chartType:     'pie';
  innerRadius:   number;    // hole radius for donut mode. 0 = solid pie. Range: 0–300. Default: 55.
  outerRadius:   number;    // outer edge radius. Range: 50–500. Default: 327.
  sliceSpacing:  number;    // gap between slices in px. Range: 0–30. Default: 8.
  labelDistance: number;    // distance from outer edge to value labels. Range: 0–200. Default: 91.
  labelLineGap?: number;    // gap px between value/% line and name line. Default: auto.
}


// ─────────────────────────────────────────────────────────────────────────────
// DEFAULTS
// ─────────────────────────────────────────────────────────────────────────────

export const PIE_DEFAULTS: ChartConfigBase<PieSpacingConfig> = {
  chartType: 'pie',
  content: {
    titles:    [makeTitle('Market Share')],
    subtitles: [makeSubtitle('Q1 2024')],
    infoBoxes: [],
  },
  animation:  { duration: 5, style: 'spinIn', textSpeed: 1 },
  background: { ...DEFAULT_BACKGROUND },
  layout: {
    chartPosition: { x: 0, y: 60 },
    chartMargin:   { top: 0, right: 0, bottom: 0, left: 0 },
  },
  // no axes — pie has no x/y axis
  legend: {
    ...DEFAULT_LEGEND,
    style: {
      ...TEXT_STYLE_DEFAULTS.legend,
      fontSize:   26,
      fontWeight: 'bold',
      color:      '#1E293B',
    },
  },
  dataLabels: {
    ...DEFAULT_DATA_LABELS,
    format: 'percentage',
    style: {
      ...TEXT_STYLE_DEFAULTS.dataLabel,
      fontSize:   28,
      fontWeight: 'bold',
      color:      '#1E293B',
    },
  },
  spacing: {
    chartType:     'pie',
    innerRadius:   55,
    outerRadius:   310,
    sliceSpacing:  6,
    labelDistance: 70,
  },
  export: { quality: '1080p', isLooping: true, canvasFormat: '16:9' },
};

// ─────────────────────────────────────────────────────────────────────────────
// SCHEMA NOTES — consumed by chart.schema.ts merger and getChartSchemaSlice()
// ─────────────────────────────────────────────────────────────────────────────

export const PIE_SCHEMA_NOTES: Record<string, SchemaNote> = {

  'pie.innerRadius': {
    description: 'Radius of the hole cut from the centre. 0 = solid pie. >0 = donut chart.',
    type:    'number',
    range:   '0–300',
    default: 55,
    unit:    'canvas pixels',
    affects: ['donut mode toggle', 'centre hole size'],
    bounds:  ['Must be strictly less than outerRadius.'],
    synergizes: ['pie.outerRadius'],
    tensions:   ['pie.labelDistance — large innerRadius + small labelDistance pushes labels inside the hole'],
    agentNote:  "Default 55 = subtle donut. 0 = traditional solid pie. If innerRadius ≥ outerRadius the chart will not render. Donut hole is often used to display a central stat in a text overlay.",
  },

  'pie.outerRadius': {
    description: 'Radius of the outer edge of the pie/donut in canvas pixels.',
    type:    'number',
    range:   '50–500',
    default: 310,
    unit:    'canvas pixels',
    affects: ['pie size', 'available space for labels'],
    bounds:  ['Must be greater than innerRadius.', 'Large values (>400) push pie near canvas edges at default layout.chartPosition.'],
    synergizes: ['pie.innerRadius', 'pie.labelDistance'],
    tensions:   ['pie.sliceSpacing — large outerRadius + large sliceSpacing = disconnected-looking slices'],
    agentNote:  "At default layout.chartPosition (960, ~590), outerRadius 310 fits comfortably. Increasing above 380 risks clipping at bottom. Account for layout.chartPosition.y offset.",
  },

  'pie.sliceSpacing': {
    description: 'Gap between adjacent slices in canvas pixels. Creates an "exploded" pie effect.',
    type:    'number',
    range:   '0–30',
    default: 6,
    unit:    'canvas pixels',
    affects: ['gap between pie slices'],
    bounds:  ['Max 30.'],
    synergizes: ['pie.outerRadius'],
    tensions:   [],
    agentNote:  "5–12 = polished. >20 = deliberately 'exploded' (intentional design, not default aesthetic).",
  },

  'pie.labelDistance': {
    description: 'Distance from the outer edge of the slice to the value labels in canvas pixels.',
    type:    'number',
    range:   '0–200',
    default: 70,
    unit:    'canvas pixels',
    affects: ['label position relative to slice edge'],
    bounds:  [],
    synergizes: ['pie.outerRadius'],
    tensions:   ['pie.innerRadius — labels placed too close to edge with large inner hole may end up inside the hole if outerRadius is small'],
    agentNote:  "Labels are placed radially outward from the slice edge. Large labelDistance on many thin slices pushes labels far apart and may cause collision. Default 70px is balanced for 4–8 slices.",
  },

  'pie.labelLineGap': {
    description: 'Gap in canvas pixels between the value/percentage line and the name label line for two-line slice labels (when dataLabels.format includes names). Applies globally to all slices; per-slice override via DataPointStyle.labelLineGap.',
    type:    'number | undefined',
    range:   'any positive number (typical: 4–20)',
    default: 'auto  (renderer calculates from fontSize)',
    unit:    'canvas pixels',
    affects: ['vertical gap between value line and name line in two-line pie labels'],
    bounds:  [],
    synergizes: ['dataLabels.style.fontSize', 'legend.style.fontSize'],
    tensions:   [],
    agentNote:  "Only relevant when slices show both a value label and a name label. Leave as undefined for auto-spacing. Set explicitly (e.g. 8–12) when auto-spacing causes the two lines to look too crowded or too far apart.",
  },

  'pie.labelSystem': {
    description: 'Pie chart radial labels are controlled by dataLabels.format. The format value determines what text appears near each slice: percentage only, value only, slice name only, or combinations. legend.* is NOT used by the pie renderer.',
    type:    'N/A — architectural note',
    range:   'N/A',
    default: null,
    unit:    'N/A',
    affects: ['radial slice labels'],
    bounds:  [],
    synergizes: ['dataLabels.format — controls ALL label content', 'dataLabels.visible — shows/hides labels'],
    tensions:   ['legend.visible has no effect on pie renderer — do not use it expecting to control slice labels'],
    agentNote:  "CRITICAL: Pie chart uses dataLabels.format for ALL radial label content. Valid formats: 'percentage' (25%), 'value' (1234), 'name' (Slice Name), 'name+value' (Slice: 1234), 'name+percent' (Slice: 25%). Do NOT set legend.visible — it has no effect on pie. Per-slice overrides: item.showValue (hide value line for one slice), item.styleOverride.labelText (custom value text), item.styleOverride.nameText (custom name text).",
  },

  'pie.sliceValues': {
    description: 'All PieDataPoint.value fields must sum to exactly 100.',
    type:    'number (per slice)',
    range:   'each value > 0; all slice values sum to 100',
    default: null,
    unit:    'percentage',
    affects: ['slice angles and proportions'],
    bounds:  ['validateConfig enforces: sum of all visible slice values must equal 100 (tolerance ±0.5).'],
    synergizes: [],
    tensions:   [],
    agentNote:  "CRITICAL: the renderer does NOT auto-normalise pie values. Sum of 98 or 103 will produce a malformed chart. Always ensure visible slice values sum to exactly 100 before saving.",
  },

};
